import jsPDF from "jspdf";
import { CompanyData, getCompanyData } from "@/components/CompanySettings";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";

interface ConversationField {
  key: string;
  label: string;
  placeholder: string;
}

interface ConversationBlock {
  id: string;
  block_id: string;
  block_label: string;
  fields: ConversationField[];
  sort_order: number;
  is_active: boolean;
}
interface ConversationData {
  id: string;
  conversation_type: string;
  conversation_date: string;
  follow_up_date: string | null;
  status: string;
  block1_erfolge: string | null;
  block1_selbsteinschaetzung: string | null;
  block1_ziele_vorjahr: string | null;
  block1_lernen_entwicklung: string | null;
  block2_planung_disposition: string | null;
  block2_material_werkzeuge: string | null;
  block2_hindernisse_prozesse: string | null;
  block2_arbeitsbelastung: string | null;
  block3_feedback_vorgesetzte: string | null;
  block3_kundenfeedback: string | null;
  block3_team_zusammenarbeit: string | null;
  block3_arbeitsschutz_psa: string | null;
  block4_mehr_eigenverantwortung: string | null;
  block4_perspektiven_rollen: string | null;
  block4_schulungen_zertifikate: string | null;
  block5_ziel1_beschreibung: string | null;
  block5_ziel1_messkriterium: string | null;
  block5_ziel1_unterstuetzung: string | null;
  block5_ziel2_beschreibung: string | null;
  block5_ziel2_messkriterium: string | null;
  block5_ziel2_unterstuetzung: string | null;
  block5_ziel3_beschreibung: string | null;
  block5_ziel3_messkriterium: string | null;
  block5_ziel3_unterstuetzung: string | null;
  besondere_bemerkungen: string | null;
  zusammenfassung_erfolge: string | null;
  zusammenfassung_ziele: string | null;
  zusammenfassung_followup: string | null;
  supervisor_name: string | null;
  supervisor_signed_at: string | null;
  supervisor_signature_url: string | null;
  employee_signed_name: string | null;
  employee_signed_at: string | null;
  employee_signature_url: string | null;
  created_at: string;
}

interface PdfOptions {
  createdByName?: string;
}

// Corporate Design Colors
const CORPORATE_COLORS = {
  primary: [25, 65, 130] as [number, number, number],
  primaryLight: [45, 90, 165] as [number, number, number],
  text: [30, 41, 59] as [number, number, number],
  textLight: [100, 116, 139] as [number, number, number],
  background: [248, 250, 252] as [number, number, number],
  backgroundAccent: [235, 242, 250] as [number, number, number],
  border: [226, 232, 240] as [number, number, number],
};

const conversationTypeLabels: Record<string, string> = {
  jahresgespraech: "Jahresgespräch",
  followup: "Follow-up-Gespräch",
  sonstiges: "Sonstiges",
};

const loadImageAsBase64 = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn("Could not load image:", e);
    return null;
  }
};

const addCompanyHeader = async (doc: jsPDF, companyData: CompanyData, logoBase64?: string | null) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  if (logoBase64) {
    try {
      doc.addImage(logoBase64, 'AUTO', 15, 8, 45, 25);
    } catch (e) {
      console.warn("Logo konnte nicht geladen werden:", e);
    }
  }
  
  doc.setDrawColor(...CORPORATE_COLORS.primary);
  doc.setLineWidth(0.8);
  doc.line(15, 36, pageWidth - 15, 36);
};

const addCompanyFooter = (doc: jsPDF, companyData: CompanyData, createdByName?: string) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageCount = doc.getNumberOfPages();
  
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    
    doc.setFillColor(...CORPORATE_COLORS.background);
    doc.rect(0, pageHeight - 35, pageWidth, 35, 'F');
    
    doc.setDrawColor(...CORPORATE_COLORS.primary);
    doc.setLineWidth(0.8);
    doc.line(15, pageHeight - 35, pageWidth - 15, pageHeight - 35);
    
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    
    let footerY = pageHeight - 28;
    doc.setTextColor(...CORPORATE_COLORS.text);
    
    if (companyData.name) {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...CORPORATE_COLORS.primary);
      doc.text(companyData.name, 15, footerY);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...CORPORATE_COLORS.text);
      footerY += 4;
    }
    
    const streetFull = [companyData.street, companyData.houseNumber].filter(Boolean).join(" ");
    if (streetFull) {
      doc.text(streetFull, 15, footerY);
      footerY += 4;
    }
    
    const cityFull = [companyData.postalCode, companyData.city].filter(Boolean).join(" ");
    if (cityFull) {
      doc.text(cityFull, 15, footerY);
      footerY += 4;
    }
    
    doc.setTextColor(...CORPORATE_COLORS.textLight);
    const contactParts: string[] = [];
    if (companyData.phone) contactParts.push(`Tel: ${companyData.phone}`);
    if (companyData.email) contactParts.push(companyData.email);
    if (contactParts.length > 0) {
      doc.text(contactParts.join(" | "), 15, footerY);
    }
    
    const now = new Date();
    const dateStr = now.toLocaleDateString("de-DE", { 
      day: "2-digit", 
      month: "2-digit", 
      year: "numeric" 
    });
    
    doc.setTextColor(...CORPORATE_COLORS.primary);
    doc.setFont("helvetica", "bold");
    doc.text("Erstellt mit HR Flash", pageWidth / 2, pageHeight - 24, { align: "center" });
    
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...CORPORATE_COLORS.textLight);
    doc.text(dateStr, pageWidth / 2, pageHeight - 20, { align: "center" });
    
    if (createdByName) {
      doc.setFontSize(6);
      doc.text(`von ${createdByName}`, pageWidth / 2, pageHeight - 16, { align: "center" });
    }
    
    doc.setFontSize(9);
    doc.setTextColor(...CORPORATE_COLORS.primary);
    doc.setFont("helvetica", "bold");
    doc.text(`Seite ${i} / ${pageCount}`, pageWidth - 15, pageHeight - 22, { align: "right" });
    
    doc.setTextColor(0, 0, 0);
  }
};

const drawSectionHeader = (doc: jsPDF, title: string, yPosition: number, pageWidth: number): number => {
  doc.setFillColor(...CORPORATE_COLORS.primary);
  doc.rect(15, yPosition - 5, pageWidth - 30, 10, 'F');
  
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text(title, 20, yPosition + 2);
  doc.setTextColor(0, 0, 0);
  
  return yPosition + 12;
};

const drawDocumentTitle = (doc: jsPDF, title: string, subtitle?: string, yPosition: number = 45): number => {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...CORPORATE_COLORS.primary);
  doc.text(title, pageWidth / 2, yPosition, { align: "center" });
  
  let currentY = yPosition + 10;
  
  if (subtitle) {
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...CORPORATE_COLORS.text);
    doc.text(subtitle, pageWidth / 2, currentY, { align: "center" });
    currentY += 12;
  }
  
  doc.setTextColor(0, 0, 0);
  return currentY;
};

const checkNewPage = (doc: jsPDF, yPosition: number, threshold: number, logoBase64?: string | null): number => {
  if (yPosition > threshold) {
    doc.addPage();
    if (logoBase64) {
      try { doc.addImage(logoBase64, 'AUTO', 15, 8, 45, 25); } catch (e) {}
    }
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setDrawColor(...CORPORATE_COLORS.primary);
    doc.setLineWidth(0.8);
    doc.line(15, 36, pageWidth - 15, 36);
    return 45;
  }
  return yPosition;
};

const drawTextField = (
  doc: jsPDF,
  label: string,
  value: string | null,
  yPosition: number,
  pageWidth: number,
  logoBase64?: string | null
): number => {
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = checkNewPage(doc, yPosition, pageHeight - 60, logoBase64);
  
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...CORPORATE_COLORS.primary);
  doc.text(label, 20, y);
  y += 5;
  
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...CORPORATE_COLORS.text);
  
  const text = value || "-";
  const lines = doc.splitTextToSize(text, pageWidth - 45);
  
  for (const line of lines) {
    y = checkNewPage(doc, y, pageHeight - 60, logoBase64);
    doc.text(line, 20, y);
    y += 5;
  }
  
  return y + 3;
};

const drawGoalSection = (
  doc: jsPDF,
  goalNum: number,
  beschreibung: string | null,
  messkriterium: string | null,
  unterstuetzung: string | null,
  yPosition: number,
  pageWidth: number,
  logoBase64?: string | null
): number => {
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = checkNewPage(doc, yPosition, pageHeight - 60, logoBase64);
  
  // Goal header
  doc.setFillColor(...CORPORATE_COLORS.backgroundAccent);
  doc.setDrawColor(...CORPORATE_COLORS.border);
  doc.setLineWidth(0.3);
  doc.roundedRect(15, y - 4, pageWidth - 30, 8, 2, 2, 'FD');
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...CORPORATE_COLORS.primary);
  doc.text(`Ziel ${goalNum}`, 20, y + 2);
  y += 12;
  
  if (beschreibung) {
    y = drawTextField(doc, "Beschreibung:", beschreibung, y, pageWidth, logoBase64);
  }
  if (messkriterium) {
    y = drawTextField(doc, "Messkriterium:", messkriterium, y, pageWidth, logoBase64);
  }
  if (unterstuetzung) {
    y = drawTextField(doc, "Unterstützung benötigt:", unterstuetzung, y, pageWidth, logoBase64);
  }
  
  return y + 2;
};

export const exportConversationToPDF = async (
  conversation: ConversationData,
  employeeName: string,
  options?: PdfOptions
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;
  
  // Company Header
  await addCompanyHeader(doc, companyData, logoBase64);
  
  // Title
  const typeLabel = conversationTypeLabels[conversation.conversation_type] || conversation.conversation_type;
  let y = drawDocumentTitle(
    doc, 
    "Mitarbeitergespräch", 
    `${typeLabel} - ${employeeName}`,
    45
  );
  
  // Meta Info Box
  y += 5;
  doc.setFillColor(...CORPORATE_COLORS.backgroundAccent);
  doc.setDrawColor(...CORPORATE_COLORS.border);
  doc.setLineWidth(0.3);
  doc.roundedRect(15, y - 4, pageWidth - 30, 22, 3, 3, 'FD');
  
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...CORPORATE_COLORS.text);
  
  const conversationDate = format(new Date(conversation.conversation_date), "dd.MM.yyyy", { locale: de });
  doc.text(`Gesprächsdatum: ${conversationDate}`, 20, y + 4);
  
  if (conversation.follow_up_date) {
    const followUpDate = format(new Date(conversation.follow_up_date), "dd.MM.yyyy", { locale: de });
    doc.text(`Follow-up Termin: ${followUpDate}`, 110, y + 4);
  }
  
  const statusLabel = conversation.status === 'completed' ? 'Abgeschlossen' : 'Entwurf';
  doc.text(`Status: ${statusLabel}`, 20, y + 12);
  
  y += 30;
  
  // Block 1: Rückblick
  y = drawSectionHeader(doc, "Block 1: Rückblick auf das vergangene Jahr", y, pageWidth);
  y = drawTextField(doc, "Erfolge / Stolz:", conversation.block1_erfolge, y, pageWidth, logoBase64);
  y = drawTextField(doc, "Selbsteinschätzung Leistung & Qualität:", conversation.block1_selbsteinschaetzung, y, pageWidth, logoBase64);
  y = drawTextField(doc, "Ziele Vorjahr:", conversation.block1_ziele_vorjahr, y, pageWidth, logoBase64);
  y = drawTextField(doc, "Lernen & Entwicklung:", conversation.block1_lernen_entwicklung, y, pageWidth, logoBase64);
  y += 5;
  
  // Block 2: Arbeitsorganisation
  y = checkNewPage(doc, y, pageHeight - 80, logoBase64);
  y = drawSectionHeader(doc, "Block 2: Arbeitsorganisation & Prozesse", y, pageWidth);
  y = drawTextField(doc, "Planung & Disposition:", conversation.block2_planung_disposition, y, pageWidth, logoBase64);
  y = drawTextField(doc, "Material & Werkzeuge:", conversation.block2_material_werkzeuge, y, pageWidth, logoBase64);
  y = drawTextField(doc, "Praktische Hindernisse / Prozesse:", conversation.block2_hindernisse_prozesse, y, pageWidth, logoBase64);
  y = drawTextField(doc, "Arbeitsbelastung:", conversation.block2_arbeitsbelastung, y, pageWidth, logoBase64);
  y += 5;
  
  // Block 3: Feedback & Team
  y = checkNewPage(doc, y, pageHeight - 80, logoBase64);
  y = drawSectionHeader(doc, "Block 3: Wertschätzung, Feedback & Team", y, pageWidth);
  y = drawTextField(doc, "Feedback durch Vorgesetzte:", conversation.block3_feedback_vorgesetzte, y, pageWidth, logoBase64);
  y = drawTextField(doc, "Kundenfeedback:", conversation.block3_kundenfeedback, y, pageWidth, logoBase64);
  y = drawTextField(doc, "Team-Zusammenarbeit:", conversation.block3_team_zusammenarbeit, y, pageWidth, logoBase64);
  y = drawTextField(doc, "Arbeitsschutz / PSA:", conversation.block3_arbeitsschutz_psa, y, pageWidth, logoBase64);
  y += 5;
  
  // Block 4: Entwicklung
  y = checkNewPage(doc, y, pageHeight - 80, logoBase64);
  y = drawSectionHeader(doc, "Block 4: Eigenverantwortung & Entwicklung", y, pageWidth);
  y = drawTextField(doc, "Mehr Eigenverantwortung:", conversation.block4_mehr_eigenverantwortung, y, pageWidth, logoBase64);
  y = drawTextField(doc, "Perspektiven / Rollen:", conversation.block4_perspektiven_rollen, y, pageWidth, logoBase64);
  y = drawTextField(doc, "Schulungen / Zertifikate:", conversation.block4_schulungen_zertifikate, y, pageWidth, logoBase64);
  y += 5;
  
  // Block 5: Ziele
  y = checkNewPage(doc, y, pageHeight - 80, logoBase64);
  y = drawSectionHeader(doc, "Block 5: Ziele & Vereinbarungen (SMART)", y, pageWidth);
  
  if (conversation.block5_ziel1_beschreibung || conversation.block5_ziel1_messkriterium || conversation.block5_ziel1_unterstuetzung) {
    y = drawGoalSection(doc, 1, conversation.block5_ziel1_beschreibung, conversation.block5_ziel1_messkriterium, conversation.block5_ziel1_unterstuetzung, y, pageWidth, logoBase64);
  }
  if (conversation.block5_ziel2_beschreibung || conversation.block5_ziel2_messkriterium || conversation.block5_ziel2_unterstuetzung) {
    y = drawGoalSection(doc, 2, conversation.block5_ziel2_beschreibung, conversation.block5_ziel2_messkriterium, conversation.block5_ziel2_unterstuetzung, y, pageWidth, logoBase64);
  }
  if (conversation.block5_ziel3_beschreibung || conversation.block5_ziel3_messkriterium || conversation.block5_ziel3_unterstuetzung) {
    y = drawGoalSection(doc, 3, conversation.block5_ziel3_beschreibung, conversation.block5_ziel3_messkriterium, conversation.block5_ziel3_unterstuetzung, y, pageWidth, logoBase64);
  }
  y += 5;
  
  // Besondere Bemerkungen
  if (conversation.besondere_bemerkungen) {
    y = checkNewPage(doc, y, pageHeight - 80, logoBase64);
    y = drawSectionHeader(doc, "Besondere Bemerkungen", y, pageWidth);
    y = drawTextField(doc, "", conversation.besondere_bemerkungen, y, pageWidth, logoBase64);
    y += 5;
  }
  
  // Zusammenfassung
  y = checkNewPage(doc, y, pageHeight - 80, logoBase64);
  y = drawSectionHeader(doc, "Zusammenfassung", y, pageWidth);
  if (conversation.zusammenfassung_erfolge) {
    y = drawTextField(doc, "Erfolge:", conversation.zusammenfassung_erfolge, y, pageWidth, logoBase64);
  }
  if (conversation.zusammenfassung_ziele) {
    y = drawTextField(doc, "Vereinbarte Ziele:", conversation.zusammenfassung_ziele, y, pageWidth, logoBase64);
  }
  if (conversation.zusammenfassung_followup) {
    y = drawTextField(doc, "Follow-up:", conversation.zusammenfassung_followup, y, pageWidth, logoBase64);
  }
  y += 10;
  
  // Unterschriften
  y = checkNewPage(doc, y, pageHeight - 100, logoBase64);
  y = drawSectionHeader(doc, "Unterschriften", y, pageWidth);
  
  const colWidth = (pageWidth - 40) / 2;
  
  // Vorgesetzter
  doc.setFillColor(...CORPORATE_COLORS.backgroundAccent);
  doc.roundedRect(15, y, colWidth, 55, 3, 3, 'F');
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...CORPORATE_COLORS.primary);
  doc.text("Vorgesetzter", 20, y + 8);
  
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...CORPORATE_COLORS.text);
  
  if (conversation.supervisor_name) {
    doc.text(`Name: ${conversation.supervisor_name}`, 20, y + 18);
  }
  if (conversation.supervisor_signed_at) {
    const signedDate = format(new Date(conversation.supervisor_signed_at), "dd.MM.yyyy HH:mm", { locale: de });
    doc.text(`Datum: ${signedDate}`, 20, y + 26);
  }
  
  if (conversation.supervisor_signature_url) {
    try {
      const sigBase64 = await loadImageAsBase64(conversation.supervisor_signature_url);
      if (sigBase64) {
        doc.addImage(sigBase64, 'PNG', 20, y + 30, 60, 20);
      }
    } catch (e) {
      console.warn("Signature could not be loaded:", e);
    }
  }
  
  // Mitarbeiter
  doc.setFillColor(...CORPORATE_COLORS.backgroundAccent);
  doc.roundedRect(20 + colWidth, y, colWidth, 55, 3, 3, 'F');
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...CORPORATE_COLORS.primary);
  doc.text("Mitarbeiter", 25 + colWidth, y + 8);
  
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...CORPORATE_COLORS.text);
  
  if (conversation.employee_signed_name) {
    doc.text(`Name: ${conversation.employee_signed_name}`, 25 + colWidth, y + 18);
  }
  if (conversation.employee_signed_at) {
    const signedDate = format(new Date(conversation.employee_signed_at), "dd.MM.yyyy HH:mm", { locale: de });
    doc.text(`Datum: ${signedDate}`, 25 + colWidth, y + 26);
  }
  
  if (conversation.employee_signature_url) {
    try {
      const sigBase64 = await loadImageAsBase64(conversation.employee_signature_url);
      if (sigBase64) {
        doc.addImage(sigBase64, 'PNG', 25 + colWidth, y + 30, 60, 20);
      }
    } catch (e) {
      console.warn("Signature could not be loaded:", e);
    }
  }
  
  // Footer
  addCompanyFooter(doc, companyData, options?.createdByName);
  
  // Save
  const fileName = `Mitarbeitergespraech_${employeeName.replace(/\s+/g, '_')}_${conversationDate.replace(/\./g, '-')}.pdf`;
  doc.save(fileName);
};

/**
 * Export a preparation overview PDF for employee conversations
 * This PDF can be given to employees before the conversation so they can prepare
 */
export const exportConversationPreparationPDF = async () => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const companyData = await getCompanyData();
  const logoBase64 = companyData.logo_url ? await loadImageAsBase64(companyData.logo_url) : null;
  
  // Load conversation field settings from database
  const { data: blocksData, error } = await supabase
    .from("conversation_field_settings")
    .select("*")
    .eq("is_active", true)
    .order("sort_order");
  
  if (error) {
    console.error("Error loading conversation settings:", error);
    throw new Error("Fehler beim Laden der Gesprächseinstellungen");
  }
  
  const blocks: ConversationBlock[] = (blocksData || []).map((block) => ({
    ...block,
    fields: typeof block.fields === 'string' ? JSON.parse(block.fields) : block.fields,
  }));
  
  // Company Header
  await addCompanyHeader(doc, companyData, logoBase64);
  
  // Title
  let y = drawDocumentTitle(
    doc, 
    "Mitarbeitergespräch", 
    "Vorbereitungsbogen für Mitarbeiter",
    45
  );
  
  // Introduction text
  y += 8;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...CORPORATE_COLORS.text);
  
  const introText = "Dieser Bogen dient Ihrer Vorbereitung auf das kommende Mitarbeitergespräch. " +
    "Bitte nehmen Sie sich Zeit, über die folgenden Themen nachzudenken und machen Sie sich ggf. Notizen. " +
    "Eine gute Vorbereitung hilft, das Gespräch konstruktiv und zielgerichtet zu führen.";
  
  const introLines = doc.splitTextToSize(introText, pageWidth - 40);
  for (const line of introLines) {
    doc.text(line, 20, y);
    y += 5;
  }
  
  y += 10;
  
  // Render each active block
  for (const block of blocks) {
    y = checkNewPage(doc, y, pageHeight - 80, logoBase64);
    
    // Section header
    y = drawSectionHeader(doc, block.block_label, y, pageWidth);
    y += 2;
    
    // Render each field in the block
    for (const field of block.fields) {
      y = checkNewPage(doc, y, pageHeight - 60, logoBase64);
      
      // Field label
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...CORPORATE_COLORS.text);
      doc.text(`• ${field.label}`, 20, y);
      y += 6;
      
      // Placeholder/hint text in lighter color
      if (field.placeholder) {
        doc.setFontSize(9);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(...CORPORATE_COLORS.textLight);
        const placeholderLines = doc.splitTextToSize(field.placeholder, pageWidth - 50);
        for (const line of placeholderLines) {
          y = checkNewPage(doc, y, pageHeight - 60, logoBase64);
          doc.text(line, 25, y);
          y += 4;
        }
      }
      
      // Empty lines for notes
      y += 3;
      doc.setDrawColor(...CORPORATE_COLORS.border);
      doc.setLineWidth(0.3);
      for (let i = 0; i < 3; i++) {
        y = checkNewPage(doc, y, pageHeight - 60, logoBase64);
        doc.line(25, y, pageWidth - 20, y);
        y += 8;
      }
      
      y += 4;
    }
    
    y += 8;
  }
  
  // Final notes section
  y = checkNewPage(doc, y, pageHeight - 80, logoBase64);
  y = drawSectionHeader(doc, "Eigene Notizen / Fragen", y, pageWidth);
  y += 5;
  
  doc.setFontSize(9);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(...CORPORATE_COLORS.textLight);
  doc.text("Hier können Sie eigene Themen, Fragen oder Anliegen notieren:", 20, y);
  y += 8;
  
  // Empty lines for personal notes
  doc.setDrawColor(...CORPORATE_COLORS.border);
  doc.setLineWidth(0.3);
  for (let i = 0; i < 8; i++) {
    y = checkNewPage(doc, y, pageHeight - 60, logoBase64);
    doc.line(20, y, pageWidth - 20, y);
    y += 10;
  }
  
  // Footer
  addCompanyFooter(doc, companyData);
  
  // Save
  const dateStr = format(new Date(), "yyyy-MM-dd", { locale: de });
  const fileName = `Mitarbeitergespraech_Vorbereitung_${dateStr}.pdf`;
  doc.save(fileName);
};
